import React from 'react';
import { ShieldCheck, Scale, Lock, Truck } from 'lucide-react';

/**
 * Terms of Sale, Age & Compliance, and Privacy.
 *
 * ⚠️ TEMPLATE / DRAFT — written as a starting point for The Machete Man. It reflects
 * common best practice and the strictest known Canadian benchmark (Manitoba's machete
 * sale regulation) but is NOT legal advice. Have a lawyer review and adapt it for every
 * province/state/municipality you sell into before relying on it.
 */

const LAST_UPDATED = 'September 23, 2026';

const Section: React.FC<{ id: string; eyebrow: string; title: string; icon: React.ElementType; children: React.ReactNode }> = ({
  id,
  eyebrow,
  title,
  icon: Icon,
  children,
}) => (
  <section id={id} className="scroll-mt-28 mb-16 lg:mb-20">
    <div className="flex items-center gap-3 mb-6 pb-5 border-b border-ink-200">
      <div className="w-10 h-10 bg-ember/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-ember" strokeWidth={1.5} />
      </div>
      <div>
        <p className="font-mono text-[10px] tracking-micro uppercase text-ember mb-1">{eyebrow}</p>
        <h2 className="font-editorial italic text-3xl text-ink-950 tracking-tight leading-none">{title}.</h2>
      </div>
    </div>
    <div className="space-y-4 text-sm text-ink-700 leading-[1.7] max-w-prose">{children}</div>
  </section>
);

const Clause: React.FC<{ heading: string; children: React.ReactNode }> = ({ heading, children }) => (
  <div>
    <p className="font-medium text-ink-950 mb-1">{heading}</p>
    <p>{children}</p>
  </div>
);

const LegalPage: React.FC = () => {
  return (
    <div className="bg-paper min-h-screen">
      {/* Header */}
      <header className="bg-forge text-paper py-16 lg:py-24 px-6 lg:px-10 relative">
        <div className="max-w-editorial mx-auto">
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember-soft mb-5">
            — Legal &amp; compliance
          </p>
          <h1 className="font-editorial italic font-light text-4xl lg:text-6xl text-paper-warm leading-[1.05] tracking-tight mb-4">
            Policies.
          </h1>
          <p className="text-sm text-paper/70 max-w-prose leading-relaxed">
            How we sell, who we sell to, and how we handle your information. Last updated {LAST_UPDATED}.
          </p>
          {/* In-page nav */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 font-mono text-[11px] tracking-wider uppercase">
            <a href="#terms" className="text-paper/70 hover:text-ember transition-colors duration-150">Terms of Sale</a>
            <a href="#compliance" className="text-paper/70 hover:text-ember transition-colors duration-150">Age &amp; Compliance</a>
            <a href="#shipping" className="text-paper/70 hover:text-ember transition-colors duration-150">Shipping &amp; Returns</a>
            <a href="#privacy" className="text-paper/70 hover:text-ember transition-colors duration-150">Privacy</a>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brass/40 to-transparent" />
      </header>

      <div className="max-w-editorial mx-auto px-6 lg:px-10 py-16 lg:py-20">

        {/* TERMS OF SALE */}
        <Section id="terms" eyebrow="— 01" title="Terms of Sale" icon={Scale}>
          <p>
            The Machete Man is a brand of{' '}
            <span className="text-ink-950 font-medium">Machetes and More Ltd.</span> By placing an order with
            The Machete Man (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;Machetes and More Ltd.&rdquo;), you agree
            to these Terms of Sale. Please read them carefully. If you do not agree, do not place an order.
          </p>
          <Clause heading="Our products are tools.">
            We sell machetes, files, and related bladed tools intended for lawful agricultural, horticultural,
            outdoor, and utility use. They are sharp and inherently dangerous if misused.
          </Clause>
          <Clause heading="You must be 18 or older.">
            Orders are sold to adults 18 years of age or older. Valid government-issued photo ID is required and
            verified at the time of delivery or pickup. We may refuse or cancel any order that cannot be age-verified.
          </Clause>
          <Clause heading="Lawful use is your responsibility.">
            You are responsible for ensuring that purchasing, possessing, carrying, and using our products is legal
            where you live and where the order is delivered. Laws vary by country, province, state, and municipality,
            and some jurisdictions restrict or regulate the sale of machetes and long-bladed tools (see Age &amp;
            Compliance below). You agree to use our products only for lawful purposes.
          </Clause>
          <Clause heading="Prices, taxes, and payment.">
            Prices are listed in Canadian dollars (CAD) and may change without notice. Applicable GST/HST and any
            other required taxes are added at invoicing. Payment is made using the methods offered at checkout —
            currently Interac e-Transfer, referencing the order number provided; orders are processed once payment
            is received and verified. Where credit or debit card payment is offered, it is processed by a
            PCI-compliant payment provider and the charge will appear on your statement as{' '}
            <span className="font-mono text-ink-950">THE MACHETE MAN</span>. We may correct pricing or product
            errors and cancel affected orders with a full refund of any amount paid.
          </Clause>
          <Clause heading="Shipping, delivery, and risk.">
            Available delivery options are shown at checkout. We may decline to ship to any region where we cannot
            lawfully or safely deliver. Risk of loss passes to you on delivery.
          </Clause>
          <Clause heading="Warranty and returns.">
            Manufacturing defects are covered under our warranty — contact us with your order number and photos and
            we will repair, replace, or refund the item. All other sales are considered final once delivered; as an
            exception, an unused item in its original condition may be eligible for return within 14 days of
            delivery if you contact us first for authorization. Blades that have been used, sharpened, or modified
            cannot be accepted, and return shipping is at the buyer&rsquo;s cost unless the item is defective.
          </Clause>
          <Clause heading="We may refuse or cancel orders.">
            We may refuse or cancel any order at our discretion, including where age or ID cannot be verified, where
            we reasonably believe a product may be used unlawfully, or due to pricing errors or stock issues.
          </Clause>
          <Clause heading="Limitation of liability.">
            To the fullest extent permitted by law, The Machete Man is not liable for any injury, loss, or damage
            arising from the use or misuse of our products, which are used entirely at your own risk. Our total
            liability for any order will not exceed the amount you paid for it. You agree to indemnify us against
            claims arising from your unlawful or negligent use of our products.
          </Clause>
          <Clause heading="Changes.">
            We may update these terms from time to time. The terms in effect at the time of your order apply.
          </Clause>
        </Section>

        {/* AGE & COMPLIANCE */}
        <Section id="compliance" eyebrow="— 02" title="Age &amp; Compliance" icon={ShieldCheck}>
          <p>
            We take the responsible sale of bladed tools seriously and aim to meet or exceed applicable requirements
            in Canada.
          </p>
          <Clause heading="18+ only, with ID on delivery.">
            Every order requires the buyer to confirm they are 18 or older at checkout, and to present valid
            government-issued photo ID when the order is handed over. Orders that fail verification are not completed.
          </Clause>
          <Clause heading="Sold as tools, for lawful use.">
            Our machetes are agricultural and outdoor tools. We do not market or sell them as weapons, and we will not
            knowingly sell to anyone who indicates they intend to use them unlawfully.
          </Clause>
          <Clause heading="Local laws vary — please check yours.">
            Some jurisdictions specifically regulate machetes and long-bladed tools. In Manitoba, the Long-Bladed
            Weapon Control Act (in force December 31, 2024) restricts the sale of blades 30&nbsp;cm (11.8&Prime;) or
            longer to anyone under 18, requires photo-ID age verification, and requires the retailer to keep a record
            of the sale for two years. Elsewhere in Canada, machetes are generally sold as tools to adults, but
            provinces and municipalities vary in how they regulate carry and possession. You are responsible for
            confirming the rules that apply where you live and receive your order.
          </Clause>
          <Clause heading="Records.">
            Where required, we retain records of sales (such as order details, buyer contact information, and age
            confirmation) to support compliance. See our Privacy policy for how this information is handled.
          </Clause>
        </Section>

        {/* SHIPPING & RETURNS */}
        <Section id="shipping" eyebrow="— 03" title="Shipping &amp; Returns" icon={Truck}>
          <p>
            We fulfil every order ourselves from our Scarborough, Ontario hub, and every hand-off
            includes the age and ID verification described above.
          </p>
          <Clause heading="Where we ship.">
            We deliver locally across the Greater Toronto Area (flat-rate courier) and ship across
            Canada by insured ground carrier where sale and delivery are lawful.
            Some regions may be unavailable at checkout where we cannot lawfully or reliably deliver.
          </Clause>
          <Clause heading="Processing and delivery times.">
            Orders are processed within 1 to 2 business days of payment verification. GTA deliveries
            typically arrive within 2 to 4 days; carrier shipments across Canada typically
            arrive within 3 to 10 business days depending on destination.
          </Clause>
          <Clause heading="Adult signature on delivery.">
            All shipments require the recipient to be 18 or older and to show valid government photo ID
            at hand-off. If delivery cannot be completed after reasonable attempts, the order is
            returned to us and refunded, less shipping costs.
          </Clause>
          <Clause heading="Warranty claims.">
            Manufacturing defects are covered under our warranty. Email photos and your order number to
            info@themacheteman.com and we will repair, replace, or refund the item — this is the right
            channel for any problem with a blade.
          </Clause>
          <Clause heading="Returns.">
            Sales are considered final once delivered. If something isn&rsquo;t right, contact us with
            your order number within 14 days of delivery — an unused item in its original condition may
            be authorized for return at our discretion. For safety and hygiene, blades that have been
            used, sharpened, or modified cannot be accepted, and unauthorized returns are not processed.
            Return shipping is at the buyer&rsquo;s cost unless the item is defective or we made an error.
          </Clause>
          <Clause heading="Refunds.">
            Where a return is authorized, refunds are issued to your original payment method once the
            item is received and inspected, normally within 5 business days. Original shipping charges
            are refundable only when the return is due to our error or a manufacturing defect.
          </Clause>
        </Section>

        {/* PRIVACY */}
        <Section id="privacy" eyebrow="— 04" title="Privacy" icon={Lock}>
          <p>
            This policy explains what personal information we collect and how we use it. We collect only what we need
            to fulfil your order and meet our obligations.
          </p>
          <Clause heading="What we collect.">
            For retail orders: your name, email, phone number, shipping address, order details, and your age
            confirmation. For wholesale accounts: business name, contact details, and business/tax number.
            e-Transfer payments run through your own bank, and any card payment we offer is processed by a
            PCI-compliant payment provider — we never see, collect, or store full card numbers ourselves.
          </Clause>
          <Clause heading="How we use it.">
            To process, deliver, and support your order; to verify eligibility and age; to communicate with you about
            your order; and to meet legal and record-keeping obligations.
          </Clause>
          <Clause heading="How it is stored.">
            Order and account data is stored using Google Firebase (Google Cloud) on our behalf. We do not sell your
            personal information, and we share it only as needed to fulfil your order or as required by law.
          </Clause>
          <Clause heading="Retention and your choices.">
            We keep order records for as long as needed to run our business and meet legal requirements. You may
            request access to, correction of, or deletion of your personal information by emailing us, subject to
            records we are required to keep.
          </Clause>
          <Clause heading="Contact.">
            Questions about these policies? Email{' '}
            <span className="text-ink-950 font-medium">info@themacheteman.com</span> or reach us on WhatsApp at
            (416) 684-0010. The Machete Man · Machetes and More Ltd., 345 Nugget Avenue, Unit 17,
            Scarborough, Ontario, Canada.
          </Clause>
        </Section>

        <p className="text-xs text-ink-500 leading-relaxed max-w-prose border-t border-ink-200 pt-8">
          These policies are provided for transparency and may be updated from time to time. They are general in
          nature and do not constitute legal advice.
        </p>
      </div>
    </div>
  );
};

export default LegalPage;
